import React, { useMemo, useState } from "react";
import { Button, Input, Popover } from "antd";
import { DownOutlined, SearchOutlined } from "@ant-design/icons";
import {
  getSupportedTimezones,
  getTimezoneOffsetLabel,
} from "../../utils/timezoneUtils";

const TimezoneSelector = ({ value, onSave, disabled = false, manualOffset = null }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pendingTimezone, setPendingTimezone] = useState(value || "");
  const timezones = useMemo(getSupportedTimezones, []);
  const visibleTimezones = useMemo(() => {
    const normalisedQuery = query.trim().toLowerCase();
    return normalisedQuery
      ? timezones.filter((timezone) =>
          timezone.toLowerCase().includes(normalisedQuery),
        )
      : timezones;
  }, [query, timezones]);

  const close = () => {
    setOpen(false);
    setQuery("");
    setPendingTimezone(value || "");
  };

  const save = () => {
    onSave(pendingTimezone);
    setOpen(false);
    setQuery("");
  };

  const content = (
    <div className="timezone-selector__popover" aria-label="Select timezone">
      <Input
        autoFocus
        prefix={<SearchOutlined />}
        placeholder="Search timezone..."
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        aria-label="Search timezone"
      />
      <div
        className="timezone-selector__options"
        role="listbox"
        aria-label="Timezones"
      >
        {visibleTimezones.length ? (
          visibleTimezones.map((timezone) => (
            <button
              type="button"
              className={`timezone-selector__option ${
                pendingTimezone === timezone
                  ? "timezone-selector__option--selected"
                  : ""
              }`}
              key={timezone}
              role="option"
              aria-selected={pendingTimezone === timezone}
              onClick={() => setPendingTimezone(timezone)}
            >
              <span>{timezone}</span>
              <span>{getTimezoneOffsetLabel(timezone)}</span>
            </button>
          ))
        ) : (
          <p className="timezone-selector__empty">No matching timezones</p>
        )}
      </div>
      <div className="timezone-selector__actions">
        <Button onClick={close}>Cancel</Button>
        <Button type="primary" onClick={save}>
          Save
        </Button>
      </div>
    </div>
  );

  const label = value ? getTimezoneOffsetLabel(value) : "Select timezone";
  if (manualOffset) {
    return (
      <span className="timezone-selector__manual-status">
        Using Manual UTC Offset
      </span>
    );
  }

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) setPendingTimezone(value || "");
        else close();
        setOpen(nextOpen);
      }}
      placement="bottomRight"
      overlayClassName="timezone-selector"
    >
      <button
        type="button"
        className="timezone-selector__trigger"
        disabled={disabled}
        aria-label="Change preferred timezone"
      >
        {label} <DownOutlined aria-hidden="true" />
      </button>
    </Popover>
  );
};

export default TimezoneSelector;
